// @/components/auth/ValidTokenCard.jsx
// Card for valid invitation tokens — shows requester, supplier info, business segment

import React from "react";
import * as motion from "motion/react-client";
import { INTRO, spring } from '@/components/reusable/animations';
import { Button, ButtonGroup } from "@/components/reusable/button";
import { Text } from "@/components/reusable/text";
import CountryFlag from '@/components/reusable/CountryFlag';
import { appStore } from "@/services/app/store";
import { useContentInvitation } from '@/hooks/content/useContentInvitation';
import { useContentScmInvite } from '@/hooks/content/useContentScmInvite';
import { useContentHotspotSectors } from '@/hooks/content/useContentHotspotSectors';
import { getHotspotSectorLabel } from '@/utils/list';
import { useLanguage } from '@/components/providers/LanguageContext';
import { getCountryName as getTranslatedCountryName } from '@/utils/countries';
import WelcomeCard from "./WelcomeCard";

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-zinc-200 rounded-sm ${className}`} />
);

export const ValidTokenCardSkeleton = () => (
  <div className="py-12">
    <div className="mx-auto max-w-6xl sm:mt-12">
      <div className="flex flex-col items-center justify-center text-center">
        <div className="space-y-4 max-w-xl mx-auto">
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        <div className="flex flex-col gap-8 mt-8 justify-center items-center">
          <div className="py-6 sm:py-0 flex-none w-12 text-center rotate-90 mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="size-8 text-zinc-300">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </div>
          <div className="flex gap-24">
            <div className="mb-2">
              <Skeleton className="h-4 w-24 mb-4" />
              <Skeleton className="h-7 w-32 mb-2" />
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="flex flex-col gap-4">
              <Skeleton className="h-4 w-32" />
              <div className="flex items-center gap-3">
                <Skeleton className="size-7 rounded-full" />
                <div>
                  <Skeleton className="h-6 w-28 mb-1" />
                  <Skeleton className="h-5 w-48" />
                </div>
              </div>
            </div>
          </div>
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
      </div>
    </div>
    <div className="mx-auto max-w-2xl text-center mt-12">
      <div className="flex justify-center gap-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-48" />
      </div>
      <div className="mt-6 space-y-2">
        <Skeleton className="h-4 w-full max-w-lg mx-auto" />
        <Skeleton className="h-4 w-full max-w-md mx-auto" />
      </div>
    </div>
  </div>
);

const ValidTokenCard = ({ tokenData, onLoginToAccept }) => {
  const invitationContent = useContentInvitation();
  const scmContent = useContentScmInvite();
  const hotspotSectorsContent = useContentHotspotSectors();
  const { locale } = useLanguage(); 

  const isDomainRegistered = tokenData?.SupplierEmailDomainRegistered;
  const countries = appStore.state.data.countries || [];
  const sectors = appStore.state.data.hotspotSectors || [];

  const countryMatch = tokenData?.RequestedCountry && countries.length > 0
    ? countries.find(c =>
      c.code === tokenData.RequestedCountry ||
      c.code?.toLowerCase() === tokenData.RequestedCountry?.toLowerCase() ||
      c.countryId === tokenData.RequestedCountry
    )
    : null;

  const sectorMatch = tokenData?.RequestedSector && sectors.length > 0
    ? sectors.find(s =>
      s.name === tokenData.RequestedSector ||
      s.name?.toLowerCase() === tokenData.RequestedSector?.toLowerCase() ||
      s.code === tokenData.RequestedSector
    )
    : null;

  const displayCountryName = getTranslatedCountryName(countryMatch?.code, locale) || tokenData?.RequestedCountry || "";

  const displaySectorName = (() => {
    const translatedLabel = getHotspotSectorLabel(hotspotSectorsContent, tokenData?.RequestedSector, null);
    if (translatedLabel) return translatedLabel;
    if (sectorMatch) {
      return getHotspotSectorLabel(hotspotSectorsContent, sectorMatch.code, sectorMatch.friendlyName || sectorMatch.name);
    }
    return tokenData?.RequestedSector || "";
  })();

  const handleLoginToAcceptClick = () => {
    if (tokenData?.LinkingId) {
      try {
        if (typeof onLoginToAccept === 'function') {
          onLoginToAccept(tokenData.LinkingId, tokenData);
        } else {
          window.location.href = '/?login=accept';
        }
      } catch {
        window.location.href = '/?login=accept';
      }
    } else {
      window.location.href = '/?login=accept';
    }
  };

  const handleRegisterClick = () => { window.location.href = '/register'; };

  const supplierName = tokenData?.SupplierName || "Your Company";
  const supplierId = tokenData?.SupplierId || "";
  const requesterName = tokenData?.RequesterCompanyName || "Your Customer";

  // Button order flips based on domain registration
  const primaryAction = isDomainRegistered ? handleLoginToAcceptClick : handleRegisterClick;
  const primaryLabel = isDomainRegistered
    ? (invitationContent?.buttonLoginExisting || "Login with existing account")
    : (invitationContent?.buttonRegisterNew || "Register new account");
  const secondaryAction = isDomainRegistered ? handleRegisterClick : handleLoginToAcceptClick;
  const secondaryLabel = isDomainRegistered
    ? (invitationContent?.buttonRegisterNew || "Register new account")
    : (invitationContent?.buttonLoginExisting || "Login with existing account");

  const guidanceMessage = isDomainRegistered
    ? `${invitationContent?.accountExistsMessage || "It looks like you or your company may already have an account."} ${invitationContent?.accountExistsGuidance || 'If this is the case, please login to accept the new invitation and complete your carbon assessment. If you do not have an account yet, please click on "Register new account" to set up a new account.'}`
    : `${invitationContent?.accountNotExistsMessage || "It looks like you do not have an account yet."} ${invitationContent?.accountNotExistsGuidance || 'If this is the case, please click on "Register new account" to set up an account. If your company already has an account, please login to accept this invitation.'}`;

  return (
    <div className="py-12">
      <WelcomeCard
        title={requesterName}
        subtitle={invitationContent?.invitationTitle || "is interested in your carbon data and invites you as their supplier"}
      >

        <div className="mt-6 relative top-1.5 flex-none w-10 text-center rotate-90 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
          </svg>
        </div>

        <div className="flex flex-col gap-8 mt-6 justify-center items-center bg-blue-50/50 p-12 rounded-2xl">



          <div className="flex gap-24">

            <div>
              <h4 className="text-xxs/5 font-medium text-zinc-500 mb-2.5">
                {scmContent?.supplierNameLabel || "Your organization"}
              </h4>
              <h4 className="text-xl/none">{supplierName}</h4>
              {supplierId && (
                <h4 className="text-sm font-medium">
                  {scmContent?.supplierIdLabel || "Supplier ID"}: {supplierId}
                </h4>
              )}
            </div>
            {(tokenData?.RequestedCountry || tokenData?.RequestedSector) && (
              <div>
                <Text className="text-xxs/5 font-medium text-zinc-500 mb-2.5">
                  {scmContent?.industryLabel || "Business Segment"}
                </Text>
                <div className="flex flex-col items-center justify-center text-center">
                  {displaySectorName && <h4 className="text-xl/none">{displaySectorName}</h4>}
                  {displayCountryName && (
                    <div className="flex gap-1 items-center">
                      {tokenData?.RequestedCountry && (
                        <CountryFlag code={tokenData.RequestedCountry} className="size-4!" />
                      )}
                      <h4 className="text-sm /none font-medium">{displayCountryName}</h4>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <h4 className="max-w-xl font-medium text-base/5">
            {invitationContent?.invitationDetailsIntro || "You have been invited by"} <span className="font-bold">{requesterName}</span>.
            {" "}
            {invitationContent?.invitationSupportText && (
              <span className="linki" dangerouslySetInnerHTML={{ __html: invitationContent.invitationSupportText }} />
            )}
          </h4>

        </div>
      </WelcomeCard >

      <motion.div
        {...INTRO}
        transition={{ ...spring, delay: 0.1 }}
        className="mx-auto max-w-3xl text-center mt-12"
      >
        <ButtonGroup className="justify-center! gap-5!">
          <Button
            variant="outline"
            onClick={secondaryAction}
          >
            {secondaryLabel}
          </Button>
          <Button onClick={primaryAction}>
            {primaryLabel}
          </Button>
        </ButtonGroup>
        <h4 className="mt-10 text-xs/4 text-zinc-400 font-medium">
          <span className="font-bold">{guidanceMessage.split('. ')[0]}.</span>{' '}
          {guidanceMessage.split('. ').slice(1).join('. ')}
        </h4>
      </motion.div>
    </div >
  );
};

export default ValidTokenCard;
